import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { displayMuscleName } from '../lib/muscleUtils';
import { translateMuscle, translateEquipment, translateBodyPart } from '../lib/muscleTranslations';
import { useLanguage } from '../contexts/LanguageContext';
import { workoutsApi } from '../api';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import ImageCropper from './ImageCropper';
import {
  ChevronLeft, Plus, Camera, X, Check, ChevronDown, Loader2
} from 'lucide-react';
import { Tooltip as UITooltip, TooltipTrigger, TooltipContent } from './ui/tooltip';


const TARGET_MUSCLES = [
  'abs','adductors','biceps','calves','cardiovascular system','delts',
  'forearms','glutes','hamstrings','lats','pectorals','quads',
  'serratus anterior','traps','triceps','upper back'
];

import { BODY_PARTS, EQUIPMENT_LIST } from '../lib/constants';


export default function CreateCustomExercise({ onBack, onCreated, prefillName = '' }) {
  const { t } = useTranslation();
  const { language } = useLanguage();

  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Step 1 fields
  const [name, setName] = useState(prefillName);
  const [bodyPart, setBodyPart] = useState('');
  const [equipment, setEquipment] = useState('');
  const [targetMuscles, setTargetMuscles] = useState([]);
  const [showMuscleDropdown, setShowMuscleDropdown] = useState(false);

  // Step 2 fields
  const [secondaryMuscles, setSecondaryMuscles] = useState([]);
  const [showSecondaryDropdown, setShowSecondaryDropdown] = useState(false);
  const [instructions, setInstructions] = useState('');

  // Photo
  const [photo, setPhoto] = useState(null);
  const [cropSrc, setCropSrc] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const fileRef = useRef(null);

  const toggleMuscle = (m, isSecondary = false) => {
    if (isSecondary) {
      setSecondaryMuscles(prev =>
        prev.includes(m) ? prev.filter(x => x !== m) : [...prev, m]
      );
    } else {
      setTargetMuscles(prev =>
        prev.includes(m) ? prev.filter(x => x !== m) : [...prev, m]
      );
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setCropSrc(ev.target.result);
      setShowCropper(true);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleCropComplete = (croppedDataUrl) => {
    setPhoto(croppedDataUrl);
    setShowCropper(false);
    setCropSrc(null);
  };

  const canProceed = name.trim().length > 0;
  const canSave = name.trim().length > 0;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setError('');
    try {
      const payload = {
        name: name.trim(),
        muscle_group: bodyPart,
        primary_muscles: targetMuscles,
        secondary_muscles: secondaryMuscles,
        equipment: equipment,
        instructions: instructions || null,
        image_url: photo || null,
        gifUrl: photo || null,
        targetMuscles: targetMuscles,
        bodyParts: bodyPart ? [bodyPart.toLowerCase()] : [],
        equipments: equipment ? [equipment.toLowerCase()] : [],
        secondaryMuscles: secondaryMuscles,
        instructions_list: instructions ? [instructions] : [],
      };

      const res = await workoutsApi.createExercise(payload);
      onCreated(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || t('customExercise.failedToCreate'));
    } finally {
      setSaving(false);
    }
  };

  // Available secondary muscles = all muscles minus selected targets
  const availableSecondary = TARGET_MUSCLES.filter(m => !targetMuscles.includes(m));

  return (
    <div className="flex flex-col h-full" data-testid="create-custom-exercise">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border flex-shrink-0">
        <UITooltip>
          <TooltipTrigger asChild>
            <button onClick={onBack} className="text-muted-foreground hover:text-foreground p-0.5" data-testid="create-exercise-back">
              <ChevronLeft className="h-5 w-5" />
            </button>
          </TooltipTrigger>
          <TooltipContent>{t('common.back')}</TooltipContent>
        </UITooltip>
        <h3 className="font-bold text-sm flex-1" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
          {t('customExercise.title')}
        </h3>
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          <span className={step >= 1 ? 'text-primary font-bold' : ''}>1</span>
          <span>/</span>
          <span className={step >= 2 ? 'text-primary font-bold' : ''}>2</span>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4" style={{ WebkitOverflowScrolling: 'touch' }}>
        {step === 1 && (
          <>
            {/* Name */}
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 block">
                {t('customExercise.exerciseName')}
              </label>
              <Input
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder={t('customExercise.namePlaceholder')}
                className="h-10 bg-muted/40 border-border focus:border-primary/50 text-sm rounded-xl"
                data-testid="create-exercise-name"
                autoFocus
              />
            </div>

            {/* Photo Upload */}
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 block">
                {t('customExercise.photoGif')}
              </label>
              {photo ? (
                <div className="relative w-20 h-20 rounded-xl overflow-hidden border border-border">
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    className="absolute inset-0 w-full h-full"
                    aria-label={t('customExercise.replacePhoto', 'Replace photo')}
                    data-testid="create-exercise-replace-photo"
                  >
                    <img src={photo} alt="Exercise" className="w-full h-full object-cover" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setPhoto(null)}
                    className="absolute -top-2 -right-2 bg-black/80 rounded-full min-h-[44px] min-w-[44px] md:min-h-[28px] md:min-w-[28px] flex items-center justify-center active:bg-black/95 hover:bg-black/95 transition-colors shadow-md z-10"
                    aria-label={t('common.delete', 'Remove photo')}
                    data-testid="create-exercise-remove-photo"
                  >
                    <X className="h-4 w-4 text-white" />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => fileRef.current?.click()}
                  className="w-20 h-20 rounded-xl border-2 border-dashed border-border hover:border-primary/50 flex flex-col items-center justify-center gap-1 transition-colors"
                  data-testid="create-exercise-upload-photo"
                >
                  <Camera className="h-5 w-5 text-muted-foreground" />
                  <span className="text-[9px] text-muted-foreground">{t('customExercise.upload')}</span>
                </button>
              )}
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFileSelect} />
            </div>

            {/* Body Part */}
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 block">
                {t('customExercise.bodyPart')}
              </label>
              <div className="flex flex-wrap gap-1.5">
                {BODY_PARTS.map(bp => (
                  <button
                    key={bp}
                    onClick={() => setBodyPart(bodyPart === bp ? '' : bp)}
                    className={`px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all
                      ${bodyPart === bp
                        ? 'bg-primary/10 text-primary border border-primary/30'
                        : 'bg-muted/60 text-muted-foreground hover:text-foreground border border-transparent hover:border-border'}`}
                    data-testid={`create-bp-${bp}`}
                  >
                    {translateBodyPart(bp, language)}
                  </button>
                ))}
              </div>
            </div>

            {/* Target Muscles */}
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 block">
                {t('customExercise.targetMuscles')}
              </label>
              {targetMuscles.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-2">
                  {targetMuscles.map(m => (
                    <Badge key={m} variant="outline" className="text-xs gap-1 px-2 py-0.5 bg-orange-500/10 text-orange-400 border-orange-500/20 cursor-pointer"
                      onClick={() => toggleMuscle(m)}>
                      {translateMuscle(m, language)}<X className="h-2.5 w-2.5" />
                    </Badge>
                  ))}
                </div>
              )}
              <button
                onClick={() => setShowMuscleDropdown(!showMuscleDropdown)}
                className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                data-testid="create-exercise-add-muscle"
              >
                <Plus className="h-3 w-3" /> {t('customExercise.addTargetMuscle')} <ChevronDown className={`h-3 w-3 transition-transform ${showMuscleDropdown ? 'rotate-180' : ''}`} />
              </button>
              {showMuscleDropdown && (
                <div className="flex flex-wrap gap-1 mt-2 p-2 rounded-xl bg-muted/30 border border-border">
                  {TARGET_MUSCLES.map(m => (
                    <button
                      key={m}
                      onClick={() => toggleMuscle(m)}
                      className={`px-2 py-1 rounded-lg text-xs font-medium transition-all
                        ${targetMuscles.includes(m)
                          ? 'bg-primary/10 text-primary border border-primary/30'
                          : 'bg-muted/60 text-muted-foreground hover:text-foreground border border-transparent hover:border-border'}`}
                    >
                      {displayMuscleName(m, language)}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Equipment */}
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 block">
                {t('customExercise.equipment')}
              </label>
              <div className="flex flex-wrap gap-1.5">
                {EQUIPMENT_LIST.map(eq => (
                  <button
                    key={eq}
                    onClick={() => setEquipment(equipment === eq ? '' : eq)}
                    className={`px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all
                      ${equipment === eq
                        ? 'bg-blue-500/15 text-blue-400 border border-blue-500/30'
                        : 'bg-muted/60 text-muted-foreground hover:text-foreground border border-transparent hover:border-border'}`}
                    data-testid={`create-eq-${eq}`}
                  >
                    {translateEquipment(eq, language)}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            {/* Summary of step 1 */}
            <div className="p-3 rounded-xl bg-muted/30 border border-border">
              <div className="flex items-center gap-3">
                {photo ? (
                  <div className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0 border border-border">
                    <img src={photo} alt={name} className="w-full h-full object-cover" />
                  </div>
                ) : (
                  <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center flex-shrink-0 border border-border">
                    <Plus className="h-5 w-5 text-muted-foreground" />
                  </div>
                )}
                <div className="min-w-0">
                  <p className="font-bold text-sm truncate">{name}</p>
                  <div className="flex flex-wrap gap-1 mt-0.5">
                    {bodyPart && <span className="text-xs px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">{translateBodyPart(bodyPart, language)}</span>}
                    {equipment && <span className="text-xs px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-400 border border-blue-500/20">{translateEquipment(equipment, language)}</span>}
                    {targetMuscles.map(m => <span key={m} className="text-xs px-1.5 py-0.5 rounded bg-orange-500/10 text-orange-400 border border-orange-500/20 capitalize">{translateMuscle(m, language)}</span>)}
                  </div>
                </div>
              </div>
            </div>

            {/* Secondary Muscles */}
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 block">
                {t('customExercise.secondaryMuscles')}
              </label>
              {secondaryMuscles.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-2">
                  {secondaryMuscles.map(m => (
                    <Badge key={m} variant="outline" className="text-xs gap-1 px-2 py-0.5 bg-purple-500/10 text-purple-400 border-purple-500/20 cursor-pointer capitalize"
                      onClick={() => toggleMuscle(m, true)}>
                      {translateMuscle(m, language)}<X className="h-2.5 w-2.5" />
                    </Badge>
                  ))}
                </div>
              )}
              <button
                onClick={() => setShowSecondaryDropdown(!showSecondaryDropdown)}
                className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                data-testid="create-exercise-add-secondary"
              >
                <Plus className="h-3 w-3" /> {t('customExercise.addSecondaryMuscle')} <ChevronDown className={`h-3 w-3 transition-transform ${showSecondaryDropdown ? 'rotate-180' : ''}`} />
              </button>
              {showSecondaryDropdown && (
                <div className="flex flex-wrap gap-1 mt-2 p-2 rounded-xl bg-muted/30 border border-border">
                  {availableSecondary.map(m => (
                    <button
                      key={m}
                      onClick={() => toggleMuscle(m, true)}
                      className={`px-2 py-1 rounded-lg text-xs font-medium transition-all capitalize
                        ${secondaryMuscles.includes(m)
                          ? 'bg-purple-500/15 text-purple-400 border border-purple-500/30'
                          : 'bg-muted/60 text-muted-foreground hover:text-foreground border border-transparent hover:border-border'}`}
                    >
                      {translateMuscle(m, language)}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Instructions */}
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 block">
                {t('customExercise.instructions')}
              </label>
              <textarea
                value={instructions}
                onChange={e => setInstructions(e.target.value)}
                placeholder={t('customExercise.instructionsPlaceholder')}
                rows={3}
                className="w-full px-3 py-2 bg-muted/40 border border-border focus:border-primary/50 rounded-xl text-sm resize-none focus:outline-none focus:ring-1 focus:ring-primary/30"
                data-testid="create-exercise-instructions"
              />
            </div>

            {error && (
              <div className="text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
                {error}
              </div>
            )}
          </>
        )}
      </div>

      {/* Footer */}
      <div className="px-4 py-3 border-t border-border flex-shrink-0">
        {step === 1 ? (
          <button
            onClick={() => setStep(2)}
            disabled={!canProceed}
            className="w-full py-3 rounded-xl bg-primary hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold text-sm transition-colors flex items-center justify-center gap-2"
            data-testid="create-exercise-next"
          >
            {t('common.next')}
            <ChevronDown className="h-4 w-4 -rotate-90" />
          </button>
        ) : (
          <div className="flex gap-2">
            <button
              onClick={() => setStep(1)}
              className="px-4 py-3 rounded-xl bg-muted/60 text-muted-foreground hover:text-foreground font-medium text-sm transition-colors border border-border"
              data-testid="create-exercise-back-step"
            >
              {t('common.back')}
            </button>
            <button
              onClick={handleSave}
              disabled={!canSave || saving}
              className="flex-1 py-3 rounded-xl bg-primary hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold text-sm transition-colors flex items-center justify-center gap-2"
              data-testid="create-exercise-save"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              {saving ? t('customExercise.creating') : t('customExercise.createAndAdd')}
            </button>
          </div>
        )}
      </div>

      {/* Image Cropper */}
      {showCropper && cropSrc && (
        <ImageCropper
          open={showCropper}
          onClose={() => { setShowCropper(false); setCropSrc(null); }}
          imageSrc={cropSrc}
          onCropComplete={handleCropComplete}
          aspectRatio={1}
          title={t('customExercise.cropPhoto')}
          uploadContext="exercise"
        />
      )}
    </div>
  );
}
